// Mother voice-line playback (monorail-vvt). Mirrors narrator.ts's audio loading,
// but mother dialog has no tiers/queueing — it's keyed by exact line text since
// that's what ui/mother-dialog.ts already receives from game/systems/npc.ts.

import { MOTHER_LINES } from '../game/systems/npc'
import { whenAudioReady, playVoiceBuffer } from './audio'

const audioBuffers = new Map<string, AudioBuffer>()

const lineToId = new Map<string, string>()
for (const [key, lines] of Object.entries(MOTHER_LINES)) {
  lines.forEach((text, i) => lineToId.set(text, `${key}_${i}`))
}

let audioDecodeStarted = false

export async function loadMotherAudio(): Promise<void> {
  if (audioDecodeStarted) return
  audioDecodeStarted = true
  const ctx = await whenAudioReady()
  const tasks: Promise<void>[] = []
  for (const id of lineToId.values()) {
    tasks.push(
      fetch(`/audio/mother/${id}.mp3`)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          // Vite's dev server SPA-falls-back to index.html for missing
          // assets; without this guard, decodeAudioData chokes on HTML
          // with a confusing EncodingError. Check the content-type so a
          // missing file reports honestly as missing.
          const ct = r.headers.get('content-type') ?? ''
          if (!ct.startsWith('audio/')) {
            throw new Error(`missing audio file (got content-type: ${ct || 'unknown'}) — run "npm run gen-mother"`)
          }
          return r.arrayBuffer()
        })
        .then(buf => ctx.decodeAudioData(buf))
        .then(audio => { audioBuffers.set(id, audio) })
        .catch(err => console.warn(`[mother] failed to load ${id}:`, err)),
    )
  }
  await Promise.all(tasks)
}

/** Plays the voice clip for this exact dialog line, if one is loaded.
 *  No-op (caption stays the only feedback) if the line has no clip yet or
 *  decoding hasn't finished — never throws on an unrecognized line. */
export function playMotherLine(text: string): void {
  const id = lineToId.get(text)
  if (!id) return
  const buf = audioBuffers.get(id)
  if (!buf) return
  playVoiceBuffer(buf)
}
